import { Box } from '@mui/material'
import type { Feature } from 'geojson'
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AppContext from '../../../context/appContext'
import {
  useCarbonInstancesGroupedByMonitoringPeriod,
  useFarmSubdivisionsList,
  useMonitoringSites,
  useOtbnAreas,
  useSamplingAreas,
} from '../../../hooks/data'
import { useMapDimensions } from '../../../hooks/useMapDimensions'
import { checkIntersectionWithPerimeter } from '../../../utils/mapDrawHandle'
import { useCustomSnackbarError } from '../../../utils/Snackbar/useCustomSnackbarError'
import { GeomanCreateEventHandler, GeomanEditEventHandler } from '../../Map/GeomanControls'
import { Map } from '../../Map/Map'
import { monitoringSitePointToLayer, noMonitoringSitePopup } from '../../Map/monitoringSiteMarkers'
import { useEligibilityCheckContext } from '../EligibilityCheck/EligibilityCheckContext'
import { getFeatureId } from '../utils'
import { useExclusionAreaContext } from './ExclusionAreaContext'
import { toMonitoringSitesFeatureCollection } from './monitoringSitesLayer'

const OTBN_QUERY = {
  includeType: [2, 3],
} as const

export const ExclusionAreaMap: FC = () => {
  const {
    exclusionAreas,
    importedExclusionAreas,
    isLoading,
    isDrawingMode,
    isEditMode,
    selectedArea,
    startDrawingOnSelected,
    handlePolygonDrawn,
    updateAreaGeometry,
    deletePolygonFromArea,
    cancelEdit,
    isExclusionVisible,
    canEditGeometry,
  } = useExclusionAreaContext()

  const { eligibilityCheckAreas, isLoadingEligibilityChecks, isEligibilityVisible } =
    useEligibilityCheckContext()
  const { currentFarm } = useContext(AppContext)
  const { mapWidth, mapHeight } = useMapDimensions()
  const [mapLoading, setMapLoading] = useState(false)
  const [layerVersion, setLayerVersion] = useState(0)
  const { handleError } = useCustomSnackbarError()

  const { data: farmSubdivisions, isLoading: isFarmSubdivisionsLoading } = useFarmSubdivisionsList(
    currentFarm.id,
  )

  const { otbnAreas, isLoading: isOtbnAreasLoading } = useOtbnAreas({
    farmId: currentFarm.id,
    query: OTBN_QUERY,
  })

  const { data: samplingAreas, isLoading: isSamplingAreasLoading } = useSamplingAreas({
    farmId: currentFarm.id,
  })

  const { groups: ciGroups, isLoading: isLoadingCIGroups } =
    useCarbonInstancesGroupedByMonitoringPeriod({ farmId: currentFarm.id })

  const { data: monitoringSites, isLoading: isMonitoringSitesLoading } = useMonitoringSites({
    farmId: currentFarm.id,
  })

  const monitoringSiteFeatures = useMemo(
    () => toMonitoringSitesFeatureCollection(monitoringSites, samplingAreas ?? []),
    [monitoringSites, samplingAreas],
  )

  // react-leaflet's GeoJSON only reacts to `style`, never to `data`, so the layer has to be
  // remounted to pick up sites or strata colours that resolve after it mounted. The key lives on
  // the layer and not on the Pane: remounting the Pane would reset the user's layer toggle.
  const monitoringSitesKey = useMemo(
    () =>
      monitoringSiteFeatures?.features
        .map(({ properties }) => `${properties.name}:${properties.color ?? ''}`)
        .join('|') ?? '',
    [monitoringSiteFeatures],
  )

  const loading =
    mapLoading ||
    isLoading ||
    isFarmSubdivisionsLoading ||
    isSamplingAreasLoading ||
    isLoadingEligibilityChecks ||
    isOtbnAreasLoading ||
    isLoadingCIGroups ||
    isMonitoringSitesLoading

  useEffect(() => {
    if (!isEditMode) {
      setLayerVersion(v => v + 1)
    }
  }, [exclusionAreas, importedExclusionAreas, eligibilityCheckAreas, isEditMode])

  const handleCreate: GeomanCreateEventHandler = useCallback(
    e => {
      setMapLoading(true)
      try {
        const newFeature = e.layer.toGeoJSON()

        if (!checkIntersectionWithPerimeter(newFeature, currentFarm?.toGeoJSON)) {
          e.layer.remove()
          setMapLoading(false)
          cancelEdit()
          return
        }

        handlePolygonDrawn(newFeature)

        setLayerVersion(v => v + 1)
        e.layer.remove()
      } catch (error) {
        handleError(error)
        cancelEdit()
        if (e.layer) {
          e.layer.remove()
        }
      } finally {
        setMapLoading(false)
      }
    },
    [currentFarm, handlePolygonDrawn, cancelEdit, handleError],
  )

  const handleDeletePolygonFromMultiPolygon = useCallback(
    (updatedFeature: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) => {
      deletePolygonFromArea(updatedFeature)
      setLayerVersion(v => v + 1)
    },
    [deletePolygonFromArea],
  )

  const handleCancelGeoman = useCallback(() => {
    cancelEdit()
  }, [cancelEdit])

  const handleEdit: GeomanEditEventHandler = useCallback(
    e => {
      const editedFeature = e.layer.toGeoJSON()
      updateAreaGeometry(editedFeature)
      setLayerVersion(v => v + 1)
    },
    [updateAreaGeometry],
  )

  return (
    <Box sx={{ position: 'relative', height: mapHeight, width: mapWidth }}>
      <Map
        scrollWheelZoom
        isLoading={loading}
        mapHeight={mapHeight}
        mapWidth={mapWidth}
        perimeter={currentFarm?.toGeoJSON}
      >
        {isExclusionVisible && exclusionAreas && (
          <Map.Layer
            key={`exclusion-areas-v${layerVersion}`}
            layer={exclusionAreas as unknown as GeoJSON.Feature}
            layerStyles={feature => {
              const hasGrazingManagement = feature?.properties?.hasGrazingManagement
              const baseColor =
                feature?.properties?.color || (hasGrazingManagement ? 'green' : 'black')
              const isCheckedInAccordion = feature?.properties?.selected === true

              if (isCheckedInAccordion) {
                return {
                  color: 'yellow',
                  fillColor: baseColor,
                  fillOpacity: 0.4,
                  weight: 4,
                }
              }

              return {
                color: baseColor,
                fillColor: baseColor,
                fillOpacity: 0.4,
                weight: 2,
              }
            }}
          />
        )}

        {importedExclusionAreas && (
          <Map.Layer
            key={`imported-exclusion-areas-v${layerVersion}`}
            layer={importedExclusionAreas as unknown as GeoJSON.Feature}
            layerStyles={feature => {
              const baseColor = feature?.properties?.color || '#2196F3'
              const isCheckedInAccordion = feature?.properties?.selected === true

              if (isCheckedInAccordion) {
                return {
                  color: 'yellow',
                  fillColor: baseColor,
                  fillOpacity: 0.4,
                  weight: 4,
                }
              }

              return {
                color: baseColor,
                fillColor: baseColor,
                fillOpacity: 0.4,
                weight: 2,
              }
            }}
          />
        )}

        {isEligibilityVisible && eligibilityCheckAreas && (
          <Map.Layer
            key={`eligibility-check-areas-v${layerVersion}`}
            layer={eligibilityCheckAreas as unknown as GeoJSON.Feature}
            layerStyles={feature => {
              const isCheckedInAccordion = feature?.properties?.selected === true
              const baseColor = feature?.properties?.color
              if (isCheckedInAccordion) {
                return {
                  color: 'yellow',
                  fillColor: baseColor,
                  fillOpacity: 0.4,
                  weight: 4,
                }
              }

              return {
                color: baseColor,
                fillColor: baseColor,
                fillOpacity: 0.4,
                weight: 2,
              }
            }}
          />
        )}

        <Map.LayersControl collapsed={false}>
          <Map.BaseLayers />

          {samplingAreas && (
            <Map.LayersControl.Pane key="Estratos" name="Estratos">
              <Map.LayersControl.GeoJSONLayer data={samplingAreas as unknown as GeoJSON.Feature} />
            </Map.LayersControl.Pane>
          )}

          {monitoringSiteFeatures && (
            <Map.LayersControl.Pane key="Sitios de monitoreo" name="Sitios de monitoreo">
              <Map.LayersControl.GeoJSONLayer
                key={`monitoring-sites-${monitoringSitesKey}`}
                data={monitoringSiteFeatures}
                pointToLayer={monitoringSitePointToLayer}
                onEachFeature={noMonitoringSitePopup}
              />
            </Map.LayersControl.Pane>
          )}

          {ciGroups.map(group => (
            <Map.LayersControl.Pane
              key={group.programMonitoringPeriodId}
              name={`Instancias ${group.programMonitoringPeriodName}`}
            >
              <Map.LayersControl.GeoJSONLayer
                data={
                  {
                    type: 'FeatureCollection',
                    features: group.carbonInstances,
                  } as unknown as GeoJSON.Feature
                }
              />
            </Map.LayersControl.Pane>
          ))}

          {farmSubdivisions.map(layer => (
            <Map.LayersControl.Pane key={layer.name} name={layer.name}>
              <Map.LayersControl.GeoJSONLayer data={layer.features as unknown as GeoJSON.Feature} />
            </Map.LayersControl.Pane>
          ))}

          {otbnAreas && otbnAreas.green && (
            <Map.LayersControl.Pane key="otbn-green" name="OTBN verde">
              <Map.LayersControl.GeoJSONLayer
                data={otbnAreas.green as unknown as GeoJSON.Feature}
              />
            </Map.LayersControl.Pane>
          )}

          {otbnAreas && otbnAreas.yellow && (
            <Map.LayersControl.Pane key="otbn-yellow" name="OTBN amarillo">
              <Map.LayersControl.GeoJSONLayer
                data={otbnAreas.yellow as unknown as GeoJSON.Feature}
              />
            </Map.LayersControl.Pane>
          )}
        </Map.LayersControl>

        <Map.GeomanControls
          editingFeatureId={canEditGeometry && selectedArea ? getFeatureId(selectedArea) : null}
          getFeatureId={getFeatureId}
          isDrawingMode={isDrawingMode}
          onCancel={handleCancelGeoman}
          onCreate={handleCreate}
          onEdit={handleEdit}
        >
          {canEditGeometry && selectedArea && (
            <Map.MultiPolygonDeleteMarkers
              feature={selectedArea as Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>}
              onDeletePolygon={handleDeletePolygonFromMultiPolygon}
            />
          )}
        </Map.GeomanControls>

        {canEditGeometry && <Map.EditModeChip />}

        <Map.FloatingActionButtons>
          {isDrawingMode && <Map.CancelEditButton onCancel={handleCancelGeoman} />}
          {canEditGeometry && (
            <>
              <Map.AddPolygonButton
                isDrawingMode={isDrawingMode}
                selectedFeature={selectedArea}
                onStartDrawing={startDrawingOnSelected}
              />
              <Map.DeleteMultiPolygonButton selectedFeature={selectedArea} />
            </>
          )}
        </Map.FloatingActionButtons>
      </Map>
    </Box>
  )
}
